import { useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { feathersClient } from '../api/client';

const usePhoneVerification = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    /**
     * Format phone number to Auth0 format (+972...)
     */
    const formatPhoneNumber = useCallback((phone) => {
        let formattedPhone = String(phone);

        if (!formattedPhone.startsWith('+')) {
            if (formattedPhone.startsWith('972')) {
                formattedPhone = '+' + formattedPhone;
            } else if (formattedPhone.startsWith('0')) {
                formattedPhone = '+972' + formattedPhone.substring(1);
            } else {
                formattedPhone = '+972' + formattedPhone;
            }
        }

        return formattedPhone;
    }, []);

    /**
     * Send OTP to phone number
     * @param {string} phoneNumber - Phone number (any format: 0501234567, 972501234567, +972501234567)
     * @returns {Promise<object>} - { success: true, phone_number: formatted }
     */
    const sendOTP = useCallback(async (phoneNumber) => {
        setLoading(true);
        setError(null);

        try {
            console.log('📱 Sending OTP to:', phoneNumber);
            const sPhoneNumber = phoneNumber.toString();
            const formattedPhone = formatPhoneNumber(sPhoneNumber);

            const result = await feathersClient.service('auth0-passwordless-sms').create({
                phone_number: formattedPhone,
                action: 'start'
            });

            console.log('✅ OTP sent successfully');

            enqueueSnackbar(
                t("otpSent") || "קוד אימות נשלח לטלפון שלך",
                { variant: 'success' }
            );

            return {
                success: true,
                phone_number: formattedPhone
            };

        } catch (err) {
            console.error('❌ Send OTP failed:', err);

            const errorMessage = err.message || t("sendCodeFailed") || "שליחת קוד נכשלה";

            enqueueSnackbar(errorMessage, { variant: 'error' });

            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [formatPhoneNumber, enqueueSnackbar, t]);

    /**
     * Verify OTP code
     * @param {string} phoneNumber - Phone number (any format)
     * @param {string} code - 6-digit OTP code
     * @returns {Promise<object>} - { success: true, access_token, id_token, phone_number }
     */
    const verifyOTP = useCallback(async (phoneNumber, code, username = null, isRegistration = false) => {
        setLoading(true);
        setError(null);

        try {
            console.log('🔐 Verifying OTP for:', phoneNumber);

            const formattedPhone = formatPhoneNumber(phoneNumber);

            const result = await feathersClient.service('auth0-passwordless-sms').create({
                phone_number: formattedPhone,
                code: code,
                username: username,
                action: 'verify',
                isRegistration: isRegistration
            });

            console.log('✅ OTP verified successfully');

            return {
                success: true,
                access_token: result.access_token,
                id_token: result.id_token,
                refresh_token: result.refresh_token,
                phone_number: formattedPhone
            };

        } catch (err) {
            console.error('❌ Verify OTP failed:', err);

            let errorMessage = t("invalidCode") || "קוד שגוי";

            if (err.message?.includes('expired')) {
                errorMessage = t("codeExpired") || "הקוד פג תוקף";
            } else if (err.message?.includes('attempts')) {
                errorMessage = t("tooManyAttempts") || "יותר מדי נסיונות";
            } else if (err.message) {
                errorMessage = err.message;
            }

            enqueueSnackbar(errorMessage, { variant: 'error' });

            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [formatPhoneNumber, enqueueSnackbar, t]);

    /**
     * Send OTP and verify in one flow (for simple use cases)
     * This is a helper that combines both steps
     */
    const sendAndVerify = useCallback(async (phoneNumber, code) => {
        if (!code) {
            // Just send OTP
            return await sendOTP(phoneNumber);
        } else {
            // Verify OTP
            return await verifyOTP(phoneNumber, code);
        }
    }, [sendOTP, verifyOTP]);

    return {
        loading,
        error,
        sendOTP,
        verifyOTP,
        sendAndVerify,
        formatPhoneNumber
    };
};

export default usePhoneVerification;